/** @jsx React.createElement */
import React, { useState, useEffect } from 'react';
import { apiGet } from '../utils/api';
import { useAuth } from './AuthContext.jsx';

const PermissionContext = React.createContext();

export const usePermissions = () => {
  const context = React.useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [permissions, setPermissions] = useState({
    // Permissions employés (nouvelles)
    canAddEmployees: false,
    canEditEmployees: false,
    canDeleteEmployees: false,
    canViewEmployees: false,
    
    // Permissions départements (nouvelles)
    canManageDepartments: false,
    canViewDepartments: false,
    
    // Permissions horaires (nouvelles)
    canViewHoraires: false,
    canManageHoraires: false,
    
    // Permissions rapports (nouvelles)
    canViewReports: false,
    canExportReports: false,
    
    // Permissions entreprise
    canManageEntreprise: false,
    canViewEntreprise: false,
    
    // Permissions rôles et permissions
    canManageRoles: false,
    canManagePermissions: false,
    
    // Permissions notifications
    canViewNotifications: false,
    canManageNotifications: false,
    
    // Accès général
    canAccessDashboard: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Vérifier si l'utilisateur est connecté
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      
      
      const response = await apiGet('/auth/me', { silent: true });
      
      if (response && response.success) {
        setUserInfo(response.user);
        
        // Logique simplifiée basée uniquement sur les rôles
        const userRole = response.user.role?.nom || response.user.roles?.[0]?.nom || 'Employé';
        
        if (userRole === 'AdminRH' || userRole === 'RH-Admin' || userRole === 'DRH') {
          // RH-Admin : Accès complet
          setPermissions({
            // Permissions employés
            canAddEmployees: true,
            canEditEmployees: true,
            canDeleteEmployees: true,
            canViewEmployees: true,
            
            // Permissions départements
            canManageDepartments: true,
            canViewDepartments: true,
            
            // Permissions horaires
            canViewHoraires: true,
            canManageHoraires: true,
            
            // Permissions rapports
            canViewReports: true,
            canExportReports: true,
            
            // Permissions entreprise
            canManageEntreprise: true,
            canViewEntreprise: true,
            
            // Permissions rôles et permissions
            canManageRoles: true,
            canManagePermissions: true,
            
            // Permissions notifications
            canViewNotifications: true,
            canManageNotifications: true,
            
            // Accès général
            canAccessDashboard: true
          });
        } else if (userRole === 'AdminRH+1' || userRole === 'RH+1' || userRole === 'Manager') {
          // Manager : Lecture seule + rapports
          setPermissions({
            // Permissions employés
            canAddEmployees: false,
            canEditEmployees: false,
            canDeleteEmployees: false,
            canViewEmployees: true,
            
            // Permissions départements
            canManageDepartments: false,
            canViewDepartments: true,
            
            // Permissions horaires
            canViewHoraires: true,
            canManageHoraires: false,
            
            // Permissions rapports
            canViewReports: true,
            canExportReports: true,
            
            // Permissions entreprise
            canManageEntreprise: false,
            canViewEntreprise: true,
            
            // Permissions rôles et permissions
            canManageRoles: false,
            canManagePermissions: false,
            
            // Permissions notifications
            canViewNotifications: true,
            canManageNotifications: false,
            
            // Accès général
            canAccessDashboard: true
          });
        } else if (userRole === 'Employé') {
          // Employé : Accès limité mais dashboard autorisé
          setPermissions({
            // Permissions employés
            canAddEmployees: false,
            canEditEmployees: false,
            canDeleteEmployees: false,
            canViewEmployees: false,
            
            // Permissions départements
            canManageDepartments: false,
            canViewDepartments: true, // Peut voir les départements
            
            // Permissions horaires
            canViewHoraires: true, // Peut voir ses horaires
            canManageHoraires: false,
            
            // Permissions rapports
            canViewReports: true, // Peut voir ses rapports
            canExportReports: false,
            
            // Permissions entreprise
            canManageEntreprise: false,
            canViewEntreprise: true, // Peut voir les infos entreprise
            
            // Permissions rôles et permissions
            canManageRoles: false,
            canManagePermissions: false,
            
            // Permissions notifications
            canViewNotifications: true, // Peut voir ses notifications
            canManageNotifications: false,
            
            // Accès général
            canAccessDashboard: true // ✅ Dashboard autorisé pour les employés
          });
        } else {
          // Rôle inconnu : Accès refusé
          setPermissions({
            canAddEmployees: false,
            canEditEmployees: false,
            canDeleteEmployees: false,
            canViewEmployees: false,
            canManageDepartments: false,
            canViewDepartments: false,
            canViewHoraires: false,
            canManageHoraires: false,
            canViewReports: false,
            canExportReports: false,
            canManageEntreprise: false,
            canViewEntreprise: false,
            canManageRoles: false,
            canManagePermissions: false,
            canViewNotifications: false,
            canManageNotifications: false,
            canAccessDashboard: false // ❌ Dashboard refusé pour rôle inconnu
          });
        }
      } else {
        setError('Erreur lors du chargement des informations utilisateur');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (permission) => {
    return permissions[permission] || false;
  };

  const hasAnyPermission = (permissionList) => {
    return permissionList.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissionList) => {
    return permissionList.every(permission => hasPermission(permission));
  };

  const canAccessDashboard = () => {
    // Toujours autoriser l'accès au dashboard pour les utilisateurs connectés
    return true;
  };

  const canManageEmployees = () => {
    return hasAnyPermission(['canAddEmployees', 'canEditEmployees', 'canDeleteEmployees']);
  };

  const canViewEmployees = () => {
    return hasPermission('canViewEmployees');
  };

  const canManageDepartments = () => {
    return hasPermission('canManageDepartments');
  };

  const canViewDepartments = () => {
    return hasPermission('canViewDepartments');
  };

  const canViewPointages = () => {
    return hasPermission('canViewReports'); // Les pointages sont dans les rapports
  };

  const canManagePointages = () => {
    return hasPermission('canExportReports');
  };

  const canViewReports = () => {
    return hasPermission('canViewReports');
  };

  const canExportReports = () => {
    return hasPermission('canExportReports');
  };

  const canManageEntreprise = () => {
    return hasPermission('canManageEntreprise');
  };

  const canViewEntreprise = () => {
    return hasPermission('canViewEntreprise');
  };

  const canManageRoles = () => {
    return hasPermission('canManageRoles');
  };

  const canManagePermissions = () => {
    return hasPermission('canManagePermissions');
  };

  const canManageHoraires = () => {
    return hasPermission('canManageHoraires');
  };

  const canViewHoraires = () => {
    return hasPermission('canViewHoraires');
  };

  const canViewNotifications = () => {
    return hasPermission('canViewNotifications');
  };

  const canManageNotifications = () => {
    return hasPermission('canManageNotifications');
  };

  // Fonction pour obtenir le nom d'affichage du rôle
  const getDisplayRoleName = () => {
    if (!userInfo) return 'Utilisateur';
    
    const userRole = userInfo.role?.nom || userInfo.roles?.[0]?.nom || 'Employé';
    const firstName = userInfo.nom_complet?.split(' ')[0] || 'Utilisateur';
    
    if (userRole === 'RH-Admin' || userRole === 'DRH') {
      return `Administrateur RH (${firstName})`;
    }
    
    if (userRole === 'RH+1' || userRole === 'Manager') {
      return `Manager (${firstName})`;
    }
    
    return userRole;
  };

  useEffect(() => {
    if (token && user) {
      console.log('🔄 Token détecté, rechargement des permissions...');
      fetchUserInfo();
    } else {
      console.log('❌ Pas de token, réinitialisation des permissions');
      setUserInfo(null);
      setPermissions({
        canAddEmployees: false,
        canEditEmployees: false,
        canDeleteEmployees: false,
        canViewEmployees: false,
        canManageDepartments: false,
        canViewDepartments: false,
        canViewHoraires: false,
        canManageHoraires: false,
        canViewPointages: false,
        canCreatePointages: false,
        canEditPointages: false,
        canDeletePointages: false,
        canViewReports: false,
        canExportReports: false,
        canManageEntreprise: false,
        canViewEntreprise: false,
        canManageRoles: false,
        canManagePermissions: false,
        canViewNotifications: false,
        canManageNotifications: false,
        canAccessDashboard: false
      });
      setLoading(false);
    }
  }, [token, user]);

  const value = {
    userInfo,
    permissions,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessDashboard,
    canManageEmployees,
    canViewEmployees,
    canManageDepartments,
    canViewDepartments,
    canViewPointages,
    canManagePointages,
    canViewReports,
    canExportReports,
    canManageEntreprise,
    canViewEntreprise,
    canManageRoles,
    canManagePermissions,
    canManageHoraires,
    canViewHoraires,
    canViewNotifications,
    canManageNotifications,
    getDisplayRoleName,
    refreshPermissions: fetchUserInfo
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};




